import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { z } from "zod";
import { supabase } from "./supabase";

export const nativeAuthRedirect = "app.samklang.mobile://auth/callback";

const NativeAuthSessionLinkSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  destination: z.enum(["home", "subscription"]).default("home"),
});

const NativeAuthUrlSchema = z.string().url().transform((value, context) => {
  const url = new URL(value);
  if (url.protocol !== "app.samklang.mobile:" || url.hostname !== "auth" || url.pathname !== "/callback") {
    context.addIssue({ code: z.ZodIssueCode.custom, message: "Ogiltig inloggningslänk." });
    return z.NEVER;
  }
  return url;
});

export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

export type NativeAuthPayload =
  | { readonly kind: "session"; readonly accessToken: string; readonly refreshToken: string; readonly destination: "home" | "subscription" }
  | { readonly kind: "code"; readonly code: string; readonly destination: "home" | "subscription" };

export function createNativeAuthSessionUrl(input: {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly destination?: "home" | "subscription";
}): string {
  const parsed = NativeAuthSessionLinkSchema.parse(input);
  const fragment = new URLSearchParams({
    access_token: parsed.accessToken,
    refresh_token: parsed.refreshToken,
    ...(parsed.destination === "subscription" ? { destination: parsed.destination } : {}),
  });
  return `${nativeAuthRedirect}#${fragment.toString()}`;
}

export function parseNativeAuthUrl(rawUrl: string): NativeAuthPayload {
  const url = NativeAuthUrlSchema.parse(rawUrl);
  const fragment = new URLSearchParams(url.hash.slice(1));
  const errorDescription = fragment.get("error_description") ?? url.searchParams.get("error_description");
  if (errorDescription) throw new Error("AUTH_LINK_REJECTED");
  const accessToken = fragment.get("access_token");
  const refreshToken = fragment.get("refresh_token");
  const destination = fragment.get("destination") === "subscription" ? "subscription" : "home";
  if (accessToken && refreshToken) return { kind: "session", accessToken, refreshToken, destination };
  const code = url.searchParams.get("code");
  if (code) return { kind: "code", code, destination };
  throw new Error("AUTH_LINK_INVALID");
}

async function importSessionFromUrl(rawUrl: string): Promise<"home" | "subscription" | null> {
  if (!supabase) return null;
  const payload = parseNativeAuthUrl(rawUrl);
  if (payload.kind === "session") {
    const { error } = await supabase.auth.setSession({ access_token: payload.accessToken, refresh_token: payload.refreshToken });
    if (error) throw new Error("AUTH_SESSION_FAILED");
    return payload.destination;
  }
  if (payload.kind === "code") {
    const { error } = await supabase.auth.exchangeCodeForSession(payload.code);
    if (error) throw new Error("AUTH_CODE_FAILED");
    return payload.destination;
  }
  return null;
}

function showDestination(destination: "home" | "subscription"): void {
  window.history.replaceState({}, "", `/${destination}`);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export async function initializeNativeAuthLinks(): Promise<void> {
  if (!isNativeApp() || !supabase) return;
  const handle = async (url: string): Promise<void> => {
    try {
      const destination = await importSessionFromUrl(url);
      if (destination) showDestination(destination);
    } catch {
      window.history.replaceState({}, "", "/login?authError=1");
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  };
  await App.addListener("appUrlOpen", ({ url }) => { void handle(url); });
  const launch = await App.getLaunchUrl();
  if (launch?.url) await handle(launch.url);
}
